import type { DatosSede, Disponibilidad, Exportacion, PaginaPrivacidad, Sector, Sede } from '../../api/ops/negocio';
import type { EstadoCanal } from '../../api/ops/canales';
import type { EstadoChip } from '../../componentes/Chip';
import { ESTADO_CANAL } from '../canales/reglas';
import type { NombreIcono } from '../../componentes/iconos';

/**
 * Lógica sin estilos de «Negocio» (1.14): resúmenes de las tarjetas, la dirección web y la ficha de una sede. Textos del
 * diseño (`diseno/plataforma/1.14-ajustes-del-negocio-y-slug.html`) salvo donde una decisión manda otra cosa (G255: la
 * zona horaria se cambia en Horario; pregunta 13 aún abierta: sin «de 3» en los cambios de dirección).
 */

export const ZONAS: Readonly<Record<string, string>> = {
    'Europe/Madrid': 'Hora de Madrid',
    'Atlantic/Canary': 'Hora de Canarias',
    'Europe/Lisbon': 'Hora de Lisboa',
    'Europe/London': 'Hora de Londres',
    'Europe/Paris': 'Hora de París',
    'Europe/Rome': 'Hora de Roma',
    'Europe/Berlin': 'Hora de Berlín',
    'America/Mexico_City': 'Hora de Ciudad de México',
    'America/Bogota': 'Hora de Bogotá',
};

export const IDIOMAS: readonly (readonly [string, string])[] = [
    ['es-ES', 'Español'],
    ['ca-ES', 'Català'],
    ['en-GB', 'English'],
    ['pt-PT', 'Português'],
];

export const PAISES: readonly (readonly [string, string])[] = [
    ['ES', 'España'],
    ['PT', 'Portugal'],
    ['AD', 'Andorra'],
    ['FR', 'Francia'],
    ['IT', 'Italia'],
    ['DE', 'Alemania'],
    ['GB', 'Reino Unido'],
    ['MX', 'México'],
    ['CO', 'Colombia'],
    ['AR', 'Argentina'],
];

/** Comunidades y ciudades autónomas de España (ISO 3166-2), para los festivos autonómicos (G251). */
export const COMUNIDADES_ES: readonly (readonly [string, string])[] = [
    ['ES-AN', 'Andalucía'],
    ['ES-AR', 'Aragón'],
    ['ES-AS', 'Asturias'],
    ['ES-IB', 'Illes Balears'],
    ['ES-CN', 'Canarias'],
    ['ES-CB', 'Cantabria'],
    ['ES-CL', 'Castilla y León'],
    ['ES-CM', 'Castilla-La Mancha'],
    ['ES-CT', 'Cataluña'],
    ['ES-EX', 'Extremadura'],
    ['ES-GA', 'Galicia'],
    ['ES-MD', 'Comunidad de Madrid'],
    ['ES-MC', 'Región de Murcia'],
    ['ES-NC', 'Navarra'],
    ['ES-PV', 'País Vasco'],
    ['ES-RI', 'La Rioja'],
    ['ES-VC', 'Comunitat Valenciana'],
    ['ES-CE', 'Ceuta'],
    ['ES-ML', 'Melilla'],
];

export const etiquetaDe = (lista: readonly (readonly [string, string])[], valor: string | null | undefined): string => lista.find(([v]) => v === valor)?.[1] ?? valor ?? '';

export const zonaDe = (tz: string): string => ZONAS[tz] ?? tz;

/** «Reservas», «Citas» o «Reservas y Citas»: los bloques `reservas` y `citas` vigentes (G170). */
export function queGestiona(bloques: readonly string[]): string {
    const r = bloques.includes('reservas');
    const c = bloques.includes('citas');
    return r && c ? 'Reservas y Citas' : r ? 'Reservas' : c ? 'Citas' : '';
}

export function resumenSedes(sedes: readonly Sede[]): string {
    const activas = sedes.filter((s) => s.status !== 'archived');
    const archivadas = sedes.length - activas.length;
    const base = activas.length === 1 ? (activas[0]?.name ?? '') : `${String(activas.length)} sedes`;
    return archivadas > 0 ? `${base} · ${String(archivadas)} archivada${archivadas > 1 ? 's' : ''}` : base;
}

export function direccionDeSede(s: Pick<Sede, 'address_line1' | 'address_line2' | 'postal_code' | 'city' | 'country'>): string {
    return [s.address_line1, s.address_line2, `${s.postal_code} ${s.city}`, etiquetaDe(PAISES, s.country)].filter((x) => x !== undefined && x.trim() !== '').join(', ');
}

/**
 * La dirección web sin `https://` (como la escribe el diseño) y su base para otras direcciones del negocio: todo lo que va
 * antes del slug en `public_url`, que calcula el servidor con el dominio de cada entorno (G118). El panel no conoce
 * dominios.
 */
export function sinEsquema(url: string): string {
    return url.replace(/^https?:\/\//, '').replace(/\/+$/, '');
}

export function baseDeDireccion(publicUrl: string, slug: string): string {
    const limpia = sinEsquema(publicUrl);
    return limpia.endsWith(`/${slug}`) ? limpia.slice(0, limpia.length - slug.length) : `${limpia}/`;
}

export const FORMATO_SLUG = /^[a-z0-9](?:[a-z0-9-]{1,48}[a-z0-9])$/;

export const TEXTOS_SLUG = {
    ayuda: 'Minúsculas sin tilde, números y guiones; de 3 a 50.',
    comprobando: 'Comprobando…',
    formato: 'Formato no válido: usa minúsculas sin tilde, números y guiones (de 3 a 50).',
    misma: 'Es la dirección que ya tienes.',
    reservada: 'Está reservada. Elige otra.',
    ocupada: 'Ya la usa otro negocio. Elige otra.',
    // G1035: recuperar una dirección antigua cuenta como uno de los 3 cambios del año.
    anterior: 'Era tuya: la recuperas. Cuenta como un cambio.',
    recuperar: 'Recuperarla cuenta como un cambio.',
    libre: (direccion: string) => `Libre: ${direccion} se puede usar.`,
} as const;

/** Lo que se escribe pasa a minúsculas y los espacios a guiones (como el diseño). */
export function normalizarSlug(valor: string): string {
    return valor.toLowerCase().replace(/\s+/g, '-');
}

export interface EstadoSlug {
    ok: boolean;
    ayuda?: string;
    error?: string;
}

/** Estado de la dirección nueva: antes de preguntar (formato y la misma) y con la respuesta de checkSlugAvailability. */
export function estadoSlug(valor: string, actual: string, base: string, comprobando: boolean, respuesta: Disponibilidad | null): EstadoSlug {
    if (valor === '') {
        return { ok: false, ayuda: TEXTOS_SLUG.ayuda };
    }
    if (!FORMATO_SLUG.test(valor)) {
        return { ok: false, error: TEXTOS_SLUG.formato };
    }
    if (valor === actual) {
        return { ok: false, ayuda: TEXTOS_SLUG.misma };
    }
    if (comprobando || respuesta === null || respuesta.slug !== valor) {
        return { ok: false, ayuda: TEXTOS_SLUG.comprobando };
    }
    switch (respuesta.reason) {
        case 'available':
            // La dirección completa la da checkSlugAvailability (`public_url`, G990): el panel no la arma.
            return { ok: true, ayuda: TEXTOS_SLUG.libre(respuesta.public_url === null ? `${base}${valor}` : sinEsquema(respuesta.public_url)) };
        case 'own_previous':
            return { ok: true, ayuda: TEXTOS_SLUG.anterior };
        case 'reserved':
            return { ok: false, error: TEXTOS_SLUG.reservada };
        case 'taken':
            return { ok: false, error: TEXTOS_SLUG.ocupada };
        case 'invalid_format':
            return { ok: false, error: TEXTOS_SLUG.formato };
    }
}

/** Cambios de dirección al año (G1007): el contador sale del servidor, `Business.slug_changes_remaining`; el panel no cuenta. */
export const CAMBIOS_SLUG_AL_ANO = 3;

/** Los textos de 1.14 para los cambios de dirección que quedan este año (G1007, G1035: recuperar una antigua también cuenta). */
export function textosCambiosSlug(quedan: number): { sub: string; usados: string; chip: string; chipEstado: 'problem' | 'neutral'; impacto: string } {
    const usados = String(Math.max(0, CAMBIOS_SLUG_AL_ANO - quedan));
    const total = String(CAMBIOS_SLUG_AL_ANO);
    const n = String(quedan);
    const despues = String(quedan - 1);
    return {
        sub: quedan <= 0 ? 'No te quedan cambios este año.' : quedan === 1 ? 'Te queda 1 cambio este año.' : `Te quedan ${n} cambios este año.`,
        usados: `${usados} de ${total}`,
        chip: quedan <= 0 ? 'Sin cambios' : quedan === 1 ? 'Queda 1' : `Quedan ${n}`,
        chipEstado: quedan <= 0 ? 'problem' : 'neutral',
        impacto: quedan - 1 <= 0 ? 'Con este no te quedará ninguno hasta el año que viene.' : `Con este te quedará ${despues} de ${total}.`,
    };
}

/** Sin cambios este año: el campo se cambia por este aviso (1.14). */
export const TEXTO_SIN_CAMBIOS_SLUG = 'Ya has hecho los 3 cambios de este año. Podrás cambiarla otra vez a partir del 1 de enero.';

/** «marzo de 2025»: hasta cuándo sirvió una dirección anterior. */
export function mesYAno(iso: string): string {
    return new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' }).format(new Date(iso));
}

/** Ficha de una sede en el formulario (todo texto; vacío = sin dato). */
export interface FichaSede {
    name: string;
    address_line1: string;
    address_line2: string;
    postal_code: string;
    city: string;
    region: string;
    region_code: string;
    country: string;
    phone: string;
}

export const FICHA_VACIA: FichaSede = { name: '', address_line1: '', address_line2: '', postal_code: '', city: '', region: '', region_code: '', country: 'ES', phone: '' };

export function fichaDe(s: Sede): FichaSede {
    return {
        name: s.name,
        address_line1: s.address_line1,
        address_line2: s.address_line2 ?? '',
        postal_code: s.postal_code,
        city: s.city,
        region: s.region ?? '',
        region_code: s.region_code ?? '',
        country: s.country,
        phone: s.phone ?? '',
    };
}

export type ErroresSede = Partial<Record<'name' | 'address_line1' | 'postal_code' | 'city', string>>;

export function erroresSede(f: FichaSede): ErroresSede {
    const e: ErroresSede = {};
    if (f.name.trim() === '') e.name = 'Escribe el nombre de la sede.';
    if (f.address_line1.trim() === '') e.address_line1 = 'Escribe la dirección.';
    if (f.postal_code.trim() === '') e.postal_code = 'Escribe el código postal.';
    if (f.city.trim() === '') e.city = 'Escribe la ciudad.';
    return e;
}

/** Lo que se manda a createLocation o updateLocation: lo vacío no se manda; la comunidad, solo en España (G251). */
export function datosDeFicha(f: FichaSede): DatosSede {
    const opcional = (v: string) => (v.trim() === '' ? undefined : v.trim());
    return {
        name: f.name.trim(),
        address_line1: f.address_line1.trim(),
        postal_code: f.postal_code.trim(),
        city: f.city.trim(),
        country: f.country,
        ...(opcional(f.address_line2) === undefined ? {} : { address_line2: f.address_line2.trim() }),
        ...(opcional(f.region) === undefined ? {} : { region: f.region.trim() }),
        region_code: f.country === 'ES' && f.region_code !== '' ? f.region_code : null,
        ...(opcional(f.phone) === undefined ? {} : { phone: f.phone.trim() }),
    };
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Teléfono con prefijo (E.164, como el contrato): «+34604263380». Se aceptan espacios al escribir. */
export const telefonoE164 = (v: string): string => v.replace(/\s+/g, '');

export function erroresContacto(email: string, telefono: string): { email?: string; phone?: string } {
    const e: { email?: string; phone?: string } = {};
    if (email.trim() !== '' && !EMAIL.test(email.trim())) e.email = 'Escribe un email válido.';
    if (telefono.trim() !== '' && !/^\+[1-9][0-9]{6,14}$/.test(telefonoE164(telefono))) e.phone = 'Escribe un teléfono válido.';
    return e;
}

/** Exportar los datos (G220): textos de cada estado. */
export function textosExportacion(e: Exportacion | null): { titulo: string; chip: string | null; chipEstado: 'confirmed' | 'pending' | 'problem' | 'neutral'; texto: string; resumen: string } {
    if (e === null) {
        return { titulo: 'Exportar todos los datos', chip: null, chipEstado: 'neutral', texto: 'Cada módulo aporta su parte. Te avisamos por email con un enlace de descarga que caduca.', resumen: 'Todos los datos del negocio en un archivo' };
    }
    switch (e.status) {
        case 'collecting':
            return { titulo: 'Preparando la exportación', chip: 'Preparando', chipEstado: 'pending', texto: 'Estamos juntando los datos de cada módulo. Te avisaremos por email cuando esté.', resumen: 'Preparando el archivo…' };
        case 'ready':
            return e.incomplete === true
                ? {
                      titulo: 'Exportación lista',
                      chip: 'Incompleta',
                      chipEstado: 'problem',
                      texto: `Falta la parte de ${(e.missing_modules ?? []).join(', ') || 'algún módulo'}: no llegó en 24 horas y te la entregamos sin ella. También te hemos mandado el enlace por email. Caduca: descárgala pronto.`,
                      resumen: 'Lista para descargar, incompleta',
                  }
                : { titulo: 'Exportación lista', chip: 'Lista', chipEstado: 'confirmed', texto: 'También te hemos mandado el enlace por email. Caduca: descárgala pronto.', resumen: 'Lista para descargar' };
        case 'expired':
            return { titulo: 'El enlace ha caducado', chip: 'Caducada', chipEstado: 'neutral', texto: 'Vuelve a exportar para tener un enlace nuevo.', resumen: 'Todos los datos del negocio en un archivo' };
        case 'failed':
            return { titulo: 'No se ha podido exportar', chip: 'Fallida', chipEstado: 'problem', texto: 'Prueba de nuevo. Si vuelve a pasar, escríbenos.', resumen: 'Todos los datos del negocio en un archivo' };
    }
}

/**
 * Sectores de `listBusinessSectors` (G270) para elegir; si el del negocio ya no se ofrece (retirado), se añade con su
 * código para que se siga viendo.
 */
export function opcionesSector(sectores: readonly Sector[], actual: string): (readonly [string, string])[] {
    const opciones = sectores.map((x) => [x.code, x.name] as const);
    return opciones.some(([c]) => c === actual) ? opciones : [[actual, actual], ...opciones];
}

/** «Privacidad de [negocio]» en el panel (G195, G268, G271). */
export interface FichaPrivacidad {
    modo: 'hosted' | 'external';
    url: string;
    legal_name: string;
    tax_id: string;
    address: string;
    contact_email: string;
    dpo_contact: string;
    extra_text: string;
}

export const PRIVACIDAD_VACIA: FichaPrivacidad = { modo: 'hosted', url: '', legal_name: '', tax_id: '', address: '', contact_email: '', dpo_contact: '', extra_text: '' };

/** Lo guardado, para el formulario: con la suya, su dirección; si no (`auto` o `hosted`), la plantilla con sus huecos. */
export function privacidadDeLaPagina(p: PaginaPrivacidad): FichaPrivacidad {
    const v = p.values ?? {};
    return {
        modo: p.mode === 'external' ? 'external' : 'hosted',
        url: sinEsquema(p.external_url ?? ''),
        legal_name: v.legal_name ?? '',
        tax_id: v.tax_id ?? '',
        address: v.address ?? '',
        contact_email: v.contact_email ?? '',
        dpo_contact: v.dpo_contact ?? '',
        extra_text: v.extra_text ?? '',
    };
}

/** Resumen de la tarjeta: la de Reservaria «Sin completar» desde el alta (G271), completada o la propia. */
export function resumenPrivacidad(p: PaginaPrivacidad): string {
    if (p.mode === 'external') {
        return `Tu política · ${sinEsquema(p.external_url ?? '')}`;
    }
    return p.mode === 'hosted' ? 'Página de Reservaria · publicada' : 'Página de Reservaria · sin completar';
}

/** La dirección de la política propia con `https://` si no lo lleva (G268: solo https). */
export function urlDePolitica(v: string): string {
    const u = v.trim();
    return /^https:\/\//i.test(u) ? u : `https://${u}`;
}

type CampoPrivacidad = 'url' | 'legal_name' | 'tax_id' | 'address' | 'contact_email';

/** Errores antes de enviar (G268): con la plantilla, los cuatro datos del responsable; con la suya, una dirección https. */
export function erroresPrivacidad(f: FichaPrivacidad): Partial<Record<CampoPrivacidad, string>> {
    const e: Partial<Record<CampoPrivacidad, string>> = {};
    if (f.modo === 'external') {
        const u = f.url.trim();
        if (u === '') e.url = 'Escribe la dirección de tu política.';
        else if (/^http:\/\//i.test(u)) e.url = 'Tiene que ser una dirección segura (https).';
        else if (!/^(https:\/\/)?[^\s/.]+(\.[^\s/.]+)+(\/\S*)?$/i.test(u)) e.url = 'Escribe una dirección válida, por ejemplo tunegocio.es/privacidad.';
        return e;
    }
    if (f.legal_name.trim() === '') e.legal_name = 'Escribe la razón social o tu nombre.';
    if (f.tax_id.trim() === '') e.tax_id = 'Escribe el NIF.';
    if (f.address.trim() === '') e.address = 'Escribe la dirección.';
    if (!EMAIL.test(f.contact_email.trim())) e.contact_email = 'Escribe un email válido.';
    return e;
}

/**
 * «Lo que pone la plantilla» (resumen provisional: el texto lo redacta la asesoría, G262). «Quién te ayuda» nombra a
 * Reservaria y solo a los proveedores que el negocio usa (G269); mientras no existan Pagos ni WhatsApp, ninguno más.
 */
export const PLANTILLA_PRIVACIDAD: readonly (readonly [NombreIcono, string, string])[] = [
    ['list-checks', 'Para qué usas sus datos', 'Gestionar sus reservas, citas, entradas y pagos, mandarle las confirmaciones y los avisos y, solo si te da permiso, ofertas.'],
    ['clock', 'Cuánto tiempo', 'Mientras sea tu cliente y, después, lo que obliga la ley. Con los plazos que pones en Clientes › Protección de datos.'],
    ['users', 'Quién te ayuda', 'Reservaria (la plataforma) y solo los proveedores que usas, como Stripe si cobras con Reservaria o Meta si conectas WhatsApp, con la lista completa de proveedores.'],
    ['scale', 'Sus derechos', 'Cómo pedir una copia, corregir o borrar sus datos, y reclamar ante la Agencia Española de Protección de Datos.'],
];

/**
 * «Oponerme» a un cambio anunciado del contrato de encargo (G931, ADR-0237): abre el correo de soporte con el asunto ya
 * puesto (versión y negocio). No hay operación que lo registre: lo atiende una persona.
 */
export function enlaceOposicion(soporte: string, version: string, negocio: { id: string; nombre: string }): string {
    // Texto del dibujo de 1.14 (G959): «Me opongo al contrato de encargo versión 2026-12 · Caseta Embrujo».
    const asunto = `Me opongo al contrato de encargo versión ${version} · ${negocio.nombre.trim() === '' ? negocio.id : negocio.nombre.trim()}`;
    return `mailto:${soporte}?subject=${encodeURIComponent(asunto)}`;
}

/** Una fila de «Para desarrolladores» (1.14, G947): un acceso directo a la pantalla donde vive cada cosa técnica. */
export interface AccesoDesarrollador {
    k: string;
    icono: NombreIcono;
    nombre: string;
    linea: string;
    /** Ruta del panel; `null` en «Exportar todos los datos», que abre su panel aquí mismo. */
    ruta: string | null;
}

/**
 * El panel de accesos de «Para desarrolladores» (1.14, G947, ADR-0240; sustituye en parte G635): por áreas, solo las filas
 * que la persona puede abrir (el permiso de la pantalla de destino), con la fila Email («Sale desde avisos@reservaria.app»,
 * G747). «Bot de Telegram» solo si el negocio lo tiene; Email y Telegram abren la ficha de su canal si se conoce. Aquí no se
 * cambia nada. Las cifras de estado del dibujo («3 activas», «1 conectada»…) las pone `cifrasDeAccesos`.
 */
export function accesosDesarrolladores(
    puede: (codigo: string) => boolean,
    exportar: boolean,
    canales: { email: string | null; telegram: string | null },
): { grupo: string; filas: AccesoDesarrollador[] }[] {
    const si = (cond: boolean, f: AccesoDesarrollador): AccesoDesarrollador[] => (cond ? [f] : []);
    const salud = puede('canales.salud.ver');
    const grupos = [
        { grupo: 'Web pública', filas: [
            ...si(puede('api.claves.gestionar'), { k: 'api', icono: 'key-round', nombre: 'Claves de API', linea: 'La API v1 para conectar tu gestor, tu ERP o una web a medida.', ruta: '/web?apartado=api' }),
            ...si(puede('web.widgets.gestionar'), { k: 'widgets', icono: 'code', nombre: 'Código para insertar un widget', linea: 'La línea que pegas en tu web.', ruta: '/web?apartado=widgets' }),
            ...si(puede('web.widgets.gestionar'), { k: 'dominios', icono: 'globe', nombre: 'Dónde se ven los widgets', linea: 'Las webs donde se puede ver cada widget (hasta 10).', ruta: '/web?apartado=widgets' }),
            ...si(puede('web.sitio.editar'), { k: 'seo', icono: 'search', nombre: 'Mapa del sitio', linea: 'Lo que leen Google y otros buscadores de tu web (SEO).', ruta: '/web?apartado=web' }),
        ] },
        { grupo: 'Canales', filas: [
            ...si(salud, { k: 'canales', icono: 'messages-square', nombre: 'Canales conectados', linea: 'WhatsApp, Instagram, Telegram y email: su salud de un vistazo.', ruta: '/canales' }),
            ...si(salud, { k: 'email', icono: 'mail', nombre: 'Email', linea: 'Sale desde avisos@reservaria.app con el nombre del negocio.', ruta: canales.email === null ? '/canales' : `/canales?canal=${encodeURIComponent(canales.email)}` }),
            ...si(salud && canales.telegram !== null, { k: 'telegram', icono: 'send', nombre: 'Bot de Telegram', linea: 'El token de @BotFather y el webhook por el que llegan los mensajes.', ruta: `/canales?canal=${encodeURIComponent(canales.telegram ?? '')}` }),
            ...si(puede('canales.plantillas.gestionar'), { k: 'plantillas', icono: 'message-square-text', nombre: 'Plantillas de Meta', linea: 'Los mensajes de WhatsApp que Meta revisa antes de enviarse.', ruta: '/canales?apartado=plantillas' }),
        ] },
        { grupo: 'Pagos', filas: si(puede('pagos.cuenta.ver'), { k: 'cuenta', icono: 'wallet', nombre: 'Cuenta de cobro', linea: 'La conexión con Stripe para cobrar señales, garantías y entradas.', ruta: '/pagos?apartado=cuenta' }) },
        { grupo: 'Datos', filas: [
            ...si(exportar, { k: 'ex', icono: 'download', nombre: 'Exportar todos los datos', linea: 'Un archivo con todo el negocio, con enlace por email.', ruta: null }),
            ...si(puede('clientes.importaciones.gestionar'), { k: 'importar', icono: 'upload', nombre: 'Importar clientes', linea: 'Desde un CSV con la plantilla, viendo antes qué pasará.', ruta: '/clientes?apartado=importar' }),
            ...si(puede('analitica.indicadores.exportar'), { k: 'exportaciones', icono: 'file-spreadsheet', nombre: 'Exportaciones de indicadores', linea: 'Tus cifras en CSV o Excel para tu hoja de cálculo.', ruta: '/indicadores?apartado=exportaciones' }),
        ] },
        { grupo: 'Equipo', filas: si(puede('equipo.tablets.gestionar'), { k: 'tablets', icono: 'tablet-smartphone', nombre: 'Tablets emparejadas', linea: 'Abren Sala, escáner y taquilla sin iniciar sesión.', ruta: '/equipo?apartado=tablets' }) },
    ];
    return grupos.filter((g) => g.filas.length > 0);
}

/** Lo que se lee para las cifras de «Para desarrolladores» (G947); `null` si no se ha podido leer o no se puede ver. */
export interface DatosAccesos {
    claves: readonly { status: string }[] | null;
    widgets: readonly { status: string }[] | null;
    canales: readonly { channel_type: string; status: EstadoCanal }[] | null;
    plantillas: readonly { status: string }[] | null;
    /** El estado de la cuenta de cobro; `sin` si nunca se conectó (404). */
    cuenta: 'sin' | 'pending' | 'active' | 'restricted' | 'disconnected' | null;
    tablets: readonly { last_seen_at?: string | null }[] | null;
}

/** La cifra y el chip de una fila de «Para desarrolladores», y su línea si la cifra la cambia (tablets). */
export interface CifraAcceso {
    estado?: string;
    chip?: [string, EstadoChip];
    linea?: string;
}

const CUENTA_COBRO: Readonly<Record<NonNullable<DatosAccesos['cuenta']>, [string, EstadoChip]>> = {
    active: ['Conectada', 'confirmed'],
    sin: ['Sin conectar', 'neutral'],
    pending: ['Alta a medias', 'pending'],
    restricted: ['Restringida', 'problem'],
    disconnected: ['Desconectada', 'problem'],
};

const plural = (n: number, uno: string, varios: string) => `${String(n)} ${n === 1 ? uno : varios}`;

/**
 * Las cifras de estado de las filas de «Para desarrolladores» (1.14, G947), como el dibujo y con las mismas reglas que
 * cada pantalla de destino: claves de API activas (13.6), widgets activos (13.4), canales conectados con los caídos
 * (11.1), el bot de Telegram si no está activo, plantillas de Meta aprobadas y con problemas (rechazadas, en pausa o
 * desactivadas; 11.4), el estado de la cuenta de cobro (4.1) y las tablets conectadas en los últimos 2 minutos (1.13).
 * Lo que no se ha podido leer no pone cifra. Email, dominios, mapa del sitio y los datos no llevan cifra.
 */
export function cifrasDeAccesos(d: DatosAccesos, ahora: number): Record<string, CifraAcceso> {
    const c: Record<string, CifraAcceso> = {};
    if (d.claves !== null) {
        c.api = { estado: plural(d.claves.filter((k) => k.status === 'active').length, 'activa', 'activas') };
    }
    if (d.widgets !== null) {
        c.widgets = { estado: plural(d.widgets.filter((w) => w.status === 'active').length, 'activo', 'activos') };
    }
    if (d.canales !== null) {
        const vivos = d.canales.filter((k) => k.status !== 'disconnected');
        const caidos = vivos.filter((k) => k.status === 'down').length;
        c.canales = { estado: plural(vivos.length, 'conectado', 'conectados'), ...(caidos > 0 ? { chip: [plural(caidos, 'caído', 'caídos'), 'problem'] } : {}) };
        const bot = vivos.find((k) => k.channel_type === 'telegram');
        if (bot !== undefined && bot.status !== 'active') {
            c.telegram = { chip: ESTADO_CANAL[bot.status] };
        }
    }
    if (d.plantillas !== null) {
        const problemas = d.plantillas.filter((t) => t.status === 'rejected' || t.status === 'paused' || t.status === 'disabled').length;
        c.plantillas = { estado: plural(d.plantillas.filter((t) => t.status === 'approved').length, 'aprobada', 'aprobadas'), ...(problemas > 0 ? { chip: [`${String(problemas)} con problemas`, 'pending'] } : {}) };
    }
    if (d.cuenta !== null) {
        c.cuenta = { chip: CUENTA_COBRO[d.cuenta] };
    }
    if (d.tablets !== null) {
        const conectadas = d.tablets.filter((t) => t.last_seen_at != null && ahora - new Date(t.last_seen_at).getTime() < 2 * 60_000).length;
        c.tablets = {
            estado: plural(conectadas, 'conectada', 'conectadas'),
            linea: `${plural(d.tablets.length, 'tablet', 'tablets')}: abren Sala, escáner y taquilla sin iniciar sesión.`,
        };
    }
    return c;
}

